// reactionHelpers.js
import { useEffect, useState, useMemo } from "react";
import { db, fieldValue } from "../../firebase/firebase.utils";

// Hook: subscribe to reactions for a single message doc
// returns { rxMap, groups, myRx }
export function useMessageReactions(docRef, currentUser) {
  const [rxMap, setRxMap] = useState({}); // { uid: {emoji, createdAt, updatedAt} }

  useEffect(() => {
    if (!docRef) return;
    const unsub = docRef.onSnapshot((snap) => {
      const data = snap.data() || {};
      setRxMap(data.reactions || {});
    });
    return () => unsub();
  }, [docRef]);

  const myRx = rxMap?.[currentUser?.uid] || null;

  // group by emoji -> { emoji, count, users: [uid,...] }
  const groups = useMemo(() => {
    const byEmoji = new Map();
    Object.entries(rxMap || {}).forEach(([uid, v]) => {
      if (!v?.emoji) return;
      if (!byEmoji.has(v.emoji)) {
        byEmoji.set(v.emoji, { emoji: v.emoji, count: 0, users: [] });
      }
      const bucket = byEmoji.get(v.emoji);
      bucket.count += 1;
      bucket.users.push(uid);
    });

    // sort: biggest count first, then emoji code
    return [...byEmoji.values()].sort(
      (a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji)
    );
  }, [rxMap]);

  return { rxMap, groups, myRx };
}

// write helper: toggle / change reaction
export async function setMyReactionOnDoc(docRef, currentUid, emoji) {
  if (!docRef || !currentUid) return;
  const path = `reactions.${currentUid}`;

  await db.runTransaction(async (tx) => {
    const snap = await tx.get(docRef);
    if (!snap.exists) {
      tx.set(docRef, { reactions: {} }, { merge: true });
    }
    const data = snap.data() || {};
    const existing = data.reactions?.[currentUid] || null;

    if (existing?.emoji === emoji) {
      // same emoji -> remove
      tx.update(docRef, {
        [path]: fieldValue.delete(),
        reaction: fieldValue.delete(), // legacy cleanup
      });
      return;
    }

    // add / update
    tx.update(docRef, {
      [path]: {
        emoji,
        createdAt: existing?.createdAt || fieldValue.serverTimestamp(),
        updatedAt: fieldValue.serverTimestamp(),
      },
      reaction: fieldValue.delete(),
    });
  });
}

// convenience remove
export function clearMyReactionOnDoc(docRef, currentUid) {
  return setMyReactionOnDoc(docRef, currentUid, null);
}
